import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Landmark, Megaphone, ChevronRight, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { api, fileUrl } from '../lib/api';
import { PageHeader, Rich, Loading, EmptyState, formatDate, NEWS_CATEGORIES } from '../components/Shared';
import { ArticleChartBlocks } from '../components/ChartBlocks';
import { useCampaignContent, TaxPromiseSection } from '../components/CampaignBlocks';
import { SafeImage } from '../components/SafeImage';

// Public transparency label for articles that carry a dollar-amount claim.
// Until the claim is confirmed against official records, the article clearly
// says so — reported figures are never presented as verified fact.
const SpendingClaimLabel = ({ post }) => {
  if (!post.has_spending_claim) return null;
  const verified = post.claim_source_status === 'verified';
  const sourceBased = post.claim_source_status === 'source_based';
  if (verified || sourceBased) {
    return (
      <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 min-w-0 max-w-full break-words [overflow-wrap:anywhere]" data-testid="claim-label-verified">
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-800 inline-flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4" /> {verified ? 'Verified' : 'Source-Based'}
        </p>
        <div className="text-xs text-emerald-900 mt-1.5 space-y-0.5">
          {post.claim_amount_verified && <p>Verified amount: <strong>{post.claim_amount_verified}</strong></p>}
          {post.claim_budget_line && <p>Budget line / page: {post.claim_budget_line}</p>}
          {post.claim_official_source_url && <p><a href={post.claim_official_source_url} target="_blank" rel="noopener noreferrer" className="underline font-bold">Official source document</a></p>}
          {post.claim_official_doc_url && <p><a href={fileUrl(post.claim_official_doc_url)} target="_blank" rel="noopener noreferrer" className="underline font-bold">Uploaded source document</a></p>}
          {post.claim_verified_date && <p>Date verified: {formatDate(post.claim_verified_date)}</p>}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-6 bg-amber-50 border border-amber-300 rounded-xl p-4 min-w-0 max-w-full break-words [overflow-wrap:anywhere]" data-testid="claim-label-unverified">
      <p className="text-xs font-bold uppercase tracking-widest text-amber-900 inline-flex items-center gap-1.5">
        <ShieldAlert className="h-4 w-4" /> Source-Based / Needs Official Budget Verification
      </p>
      <p className="text-xs sm:text-sm text-amber-900 mt-1.5 leading-relaxed">
        {post.claim_disclaimer || 'The reported cost referenced in this article comes from a public statement and should be verified against official City of Kawartha Lakes budget or procurement records. It is not presented as verified fact.'}
      </p>
      {post.claim_amount_reported && <p className="text-xs text-amber-900 mt-1.5">Reported amount: <strong>{post.claim_amount_reported}</strong> (reported, not yet verified)</p>}
    </div>
  );
};

export const NewsArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const campaign = useCampaignContent();

  useEffect(() => {
    api.get(`/news/${slug}`).then((r) => {
      setPost(r.data);
      // Canonical redirect: legacy slugs resolve to the article's current URL
      if (r.data?.slug && r.data.slug !== slug) navigate(`/newsroom/${r.data.slug}`, { replace: true });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <Loading />;
  if (!post) return <EmptyState title="Story not found" subtitle="It may have been unpublished." />;

  return (
    <div className="bg-white overflow-x-clip">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12 min-w-0">
        <Link to="/newsroom" className="inline-flex items-center text-sm font-bold text-[#1a2744] mb-6"><ChevronLeft className="h-4 w-4" /> Newsroom</Link>
        <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] break-words">{NEWS_CATEGORIES[post.category] || post.category}</p>
        <h1 className="font-serif font-black text-[#1a2744] text-3xl sm:text-4xl leading-tight mt-2 break-words" data-testid="article-title">{post.title}</h1>
        {post.dek && <p className="text-lg text-slate-600 mt-3 leading-relaxed break-words">{post.dek}</p>}
        <div className="flex items-center gap-3 mt-4 text-sm text-slate-500">
          <span className="font-semibold">{post.author || 'Campaign Team'}</span>
          <span>·</span>
          <span>{formatDate(post.published_at)}</span>
        </div>
        <Separator className="my-6" />
        {post.image_url && <SafeImage src={post.image_url} alt="" mode="hide" className="w-full max-w-full h-auto rounded-2xl mb-6 max-h-96 object-cover" testid="news-article-image" />}
        <SpendingClaimLabel post={post} />
        <div className="mt-2 min-w-0 max-w-full">
          <Rich html={post.body_html} className="text-base" />
        </div>

        {/* Chart blocks — data with sources, mobile-safe */}
        <ArticleChartBlocks blocks={post.chart_blocks} />

        {/* Why it matters / How to respond — civic action framing */}
        {post.why_matters && (
          <div className="mt-8 bg-slate-50 border-l-4 border-[#C9A84C] rounded-r-xl p-5" data-testid="article-why-matters">
            <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] mb-1.5">Why it matters</p>
            <p className="text-sm text-slate-700 leading-relaxed">{post.why_matters}</p>
          </div>
        )}
        {post.how_to_respond && (
          <div className="mt-4 bg-[#1a2744] rounded-xl p-5" data-testid="article-how-to-respond">
            <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] mb-1.5">How to respond</p>
            <p className="text-sm text-slate-200 leading-relaxed">{post.how_to_respond}</p>
            <div className="flex flex-wrap gap-3 mt-3">
              <Link to="/have-your-say" className="text-sm font-bold text-[#C9A84C] underline underline-offset-4">How to Have Your Say</Link>
              <Link to="/issue-tracker/report" className="text-sm font-bold text-[#C9A84C] underline underline-offset-4">Report an Issue</Link>
            </div>
          </div>
        )}

        {post.source_url && <p className="text-xs text-slate-400 mt-6 break-all">Source: <a href={post.source_url} className="underline [overflow-wrap:anywhere]" target="_blank" rel="noopener noreferrer">{post.source_url}</a></p>}

        {/* Steve's Tax Promise — campaign message block */}
        {campaign?.tax_promise?.sections?.newsroom_sidebar !== false && (
          <div className="mt-8"><TaxPromiseSection promise={campaign?.tax_promise} compact /></div>
        )}
      </div>
    </div>
  );
};

const DESK_SECTIONS = [
  { label: 'City Hall Watch', to: '/city-hall-watch', icon: Landmark },
  { label: "Who's Running in 2026", to: '/whos-running', icon: Megaphone },
  { label: 'Council Meetings', to: '/meetings', icon: Megaphone },
  { label: 'Tax Tracker', to: '/tax-tracker', icon: Megaphone },
  { label: 'Infrastructure', to: '/infrastructure', icon: Megaphone },
  { label: 'Public Alerts', to: '/alerts', icon: Megaphone },
  { label: 'Have Your Say', to: '/have-your-say', icon: Megaphone },
];

export default function Newsroom() {
  const [category, setCategory] = useState('all');
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/news?category=${category}`).then((r) => setPosts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [category]);

  const filtered = posts.filter((p) => !search || `${p.title} ${p.dek}`.toLowerCase().includes(search.toLowerCase()));
  const lead = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p !== lead);

  return (
    <div>
      <PageHeader pageKey="newsroom" kicker="THE KAWARTHA PEOPLE'S DESK" title="Civic News, Plain Language" subtitle="The People's Hub for Kawartha Lakes Accountability. A digital local newsroom for Ward 8." />

      {/* People's Desk section rail */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2" data-testid="peoples-desk-rail">
            {DESK_SECTIONS.map((s) => (
              <Link key={s.to} to={s.to} className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-[#1a2744] hover:bg-slate-100 uppercase tracking-wide" data-testid={`desk-rail-${s.to.replace(/\//g, '')}`}>
                {s.label} <ChevronRight className="h-3 w-3 text-[#C9A84C]" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-6">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList className="bg-white border border-slate-200 h-auto p-1 flex-wrap justify-start" data-testid="newsroom-section-tabs">
              <TabsTrigger value="all" className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white">All</TabsTrigger>
              {Object.entries(NEWS_CATEGORIES).filter(([v]) => v !== 'imported').map(([v, l]) => (
                <TabsTrigger key={v} value={v} className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white">{l}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search stories…" className="h-11 bg-white sm:max-w-xs" data-testid="newsroom-search-input" />
        </div>

        {loading ? <Loading /> : filtered.length === 0 ? (
          <EmptyState title="No stories yet" subtitle="Check back soon. The newsroom updates regularly." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {lead && (
              <Link to={`/newsroom/${lead.slug}`} className="lg:col-span-7 group" data-testid="newsroom-lead-story">
                <Card className="overflow-hidden card-lift h-full">
                  {lead.image_url && <SafeImage src={lead.image_url} alt="" className="w-full h-56 sm:h-72 object-cover" testid="newsroom-lead-image" />}
                  <CardContent className="p-5 sm:p-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C]">{NEWS_CATEGORIES[lead.category] || lead.category}</p>
                    <h2 className="font-serif font-black text-[#1a2744] text-xl sm:text-2xl leading-tight mt-2 group-hover:underline decoration-[#C9A84C] decoration-2 underline-offset-4">{lead.title}</h2>
                    {lead.dek && <p className="text-slate-600 mt-2 leading-relaxed">{lead.dek}</p>}
                    <p className="text-xs text-slate-500 mt-3">{lead.author || 'Campaign Team'} · {formatDate(lead.published_at)}</p>
                  </CardContent>
                </Card>
              </Link>
            )}
            <div className="lg:col-span-5 space-y-4">
              {rest.map((post) => (
                <Link key={post.id} to={`/newsroom/${post.slug}`} className="block group" data-testid="newsroom-story-card">
                  <Card className="card-lift">
                    <CardContent className="p-4 flex gap-4">
                      {post.image_url && <SafeImage src={post.image_url} alt="" className="h-20 w-24 object-cover rounded-lg shrink-0" testid="newsroom-card-image" />}
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#C9A84C]">{NEWS_CATEGORIES[post.category] || post.category}</p>
                        <p className="font-serif font-bold text-[#1a2744] leading-snug mt-0.5 group-hover:underline decoration-[#C9A84C] decoration-2 underline-offset-4 line-clamp-2">{post.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{formatDate(post.published_at)}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
